import { HERO_BG, SLOGAN, WHATSAPP_LINK, WHATSAPP_PASSENGERS_GROUP, LOGO_URL, REGION } from "../../lib/troy";
import { MessageCircle, ArrowRight, ShieldCheck, Zap, ExternalLink } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center overflow-hidden pt-20 grain"
    >
      {/* Background */}
      <div
        className="absolute inset-0 -z-10 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black via-black/85 to-black/40" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_30%,rgba(255,204,0,0.15),transparent_55%)]" />

      {/* Marca d'água da logo Troy */}
      <img
        src={LOGO_URL}
        alt=""
        aria-hidden="true"
        className="absolute -left-24 top-1/2 -translate-y-1/2 w-[540px] max-w-none opacity-[0.06] -z-10 pointer-events-none select-none [mask-image:radial-gradient(circle,black_30%,transparent_75%)]"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 order-2 lg:order-1">
          <div className="reveal reveal-delay-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FFCC00]/40 bg-[#FFCC00]/5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#FFCC00] animate-pulse" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#FFCC00] font-bold">
              Grupo de Transporte • General Carneiro
            </span>
          </div>

          <p
            data-testid="hero-slogan"
            className="reveal reveal-delay-1 font-display text-[#FFCC00] text-xl sm:text-2xl font-bold mb-4 tracking-tight"
          >
            {SLOGAN}
          </p>

          <h1
            data-testid="hero-title"
            className="reveal reveal-delay-2 font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter text-balance"
          >
            Troy Transportes —{" "}
            <span className="text-[#FFCC00]">sua melhor opção</span> em corridas{" "}
            <span className="relative inline-block">
              <span className="relative z-10">rápidas e seguras.</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#FF3B30]/70 -z-0 skew-x-[-6deg]" />
            </span>
          </h1>

          <p className="reveal reveal-delay-3 mt-6 max-w-xl text-lg text-white/70 leading-relaxed">
            Entre no nosso grupo oficial de passageiros, peça sua corrida e um
            motorista disponível responde em segundos. Atendemos{" "}
            <strong className="text-white">General Carneiro e regiões próximas</strong>{" "}
            — sem app, sem espera, sem dinâmica. E mais: pedindo corrida pelo
            grupo você <span className="text-[#FFCC00] font-bold">concorre a prêmios todo mês</span>. 🎁
          </p>

          <div className="reveal reveal-delay-3 mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={WHATSAPP_PASSENGERS_GROUP}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="hero-group-cta"
              className="group inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-bold py-4 px-8 rounded-full hover:bg-[#E6B800] transition-all duration-300 transform hover:-translate-y-1 shadow-[0_0_32px_rgba(255,204,0,0.4)]"
            >
              <ExternalLink className="w-5 h-5" />
              Entrar no grupo e pedir corrida
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="hero-whatsapp-cta"
              className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/15 text-white font-bold py-4 px-8 rounded-full hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Falar direto no WhatsApp
            </a>
          </div>

          <div className="reveal reveal-delay-4 mt-10 flex flex-wrap gap-6 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#FFCC00]" />
              Resposta em minutos
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#FFCC00]" />
              Motoristas verificados
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-[#FFCC00]" />
              Atendimento direto
            </div>
          </div>
        </div>

        {/* Logo em destaque — capa da marca */}
        <div className="lg:col-span-4 order-1 lg:order-2 flex justify-center">
          <div className="relative reveal reveal-delay-2 w-56 sm:w-72 lg:w-full">
            <div className="absolute -inset-6 bg-[#FF3B30]/25 blur-3xl rounded-full animate-[pulseGlow_3.5s_ease-in-out_infinite]" />
            <div className="absolute -inset-3 rounded-[2rem] bg-[radial-gradient(circle,rgba(255,204,0,0.25),transparent_70%)]" />
            <div className="relative headlight-sweep overflow-hidden rounded-[2rem] border border-[#FFCC00]/30 bg-black/60 backdrop-blur-xl p-6 sm:p-8 shadow-[0_0_60px_rgba(255,204,0,0.18)]">
              <img
                src={LOGO_URL}
                alt="Troy Transportes"
                data-testid="hero-logo"
                className="w-full aspect-square object-contain drop-shadow-[0_0_34px_rgba(255,59,48,0.5)]"
              />
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#FFCC00] text-black text-[10px] font-black tracking-widest">
                OFICIAL
              </div>
              <div className="mt-4 text-center">
                <p className="font-display text-2xl font-black tracking-tight leading-none">
                  TROY <span className="text-[#FFCC00]">TRANSPORTES</span>
                </p>
                <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mt-1.5 font-semibold">
                  {SLOGAN}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* bottom fade */}
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-[#08080A]" />
    </section>
  );
}
